import type { ModuleId } from "./RunState.ts";
import type { OperatorId } from "./operatorIds.ts";
import { DEFAULT_INITIALS } from "./initials.ts";
import { L_UI_SCALE } from "../utils/uiScale.ts";

/**
 * Persistent state across runs — serialized to StorageAdapter.
 * Follows Hades meta-progression pattern (D-028, D-030).
 */

export type AchievementId =
  | "first_contact"
  | "clean_sweep"
  | "near_death"
  | "card_collector"
  | "veteran"
  | "combo_master"
  | "skill_seeker"
  | "perfect_defense"
  | "hardcore";

export interface Achievement {
  id: AchievementId;
  unlocked: boolean;
  progress: number;
  target: number;
}

export interface HighscoreEntry {
  score: number;
  wave: number;
  operator: OperatorId;
  date: string;
  initials: string;
  /**
   * The threat level the run was played on — part of the score's IDENTITY,
   * not decoration (owner, 2026-08-16).
   *
   * Once the threat contract gives the levels real mechanics, a sandbox run
   * (0.85x enemy HP) and a fallout run (1.45x) are no longer comparable, so
   * every ranking that mixes them is meaningless. The fix is SEPARATION, never
   * normalisation: no score multipliers anywhere, the level is simply part of
   * the comparison key. Local top-10 is per level; "new highscore?" is per
   * operator AND level.
   *
   * Entries written before schema v8 carry no level; the v7→v8 migration fills
   * `containment`, and that is provable rather than assumed — until this round
   * all five levels were mechanically identical (`sim-threat-level-neutrality`),
   * so every historical run IS a containment run.
   */
  threatLevel: ThreatLevelId;
}

/**
 * The five threat levels (Register S2 "Containment", owner-pick 2026-08-13;
 * spec `2026-threat-profile-model`). Order is part of the contract — AC-1
 * pins this exact tuple and sequence, not just the membership.
 *
 * Phase 1 (this spec, D-352): identity + persistence only. All five levels
 * are mechanically IDENTICAL — proven at the sim level (AC-5). Do not add a
 * modifier field or table here; requirement §6 forbids unused fake mechanics.
 *
 * Phase 2 intent, documented but INERT — no code reads these numbers yet
 * (D-352): +30% enemy HP / +15% enemy speed / -20% credit reward, scaling
 * with level.
 */
export const THREAT_LEVEL_IDS = [
  "sandbox",
  "containment",
  "lockdown",
  "overrun",
  "fallout",
] as const;

export type ThreatLevelId = (typeof THREAT_LEVEL_IDS)[number];

/** Phase 1 ships no selector — every save carries this value (D-352). */
export const DEFAULT_THREAT_LEVEL: ThreatLevelId = "containment";

export interface GameSettings {
  language: "en" | "de";
  // Audio split (PT-19 / schema v3): the old single `audioMuted` became two
  // independent buses + volumes (music sits under SFX). `sfxMuted` carries the
  // legacy in-game "sound off".
  sfxMuted: boolean;
  musicMuted: boolean;
  sfxVolume: number; // 0..1
  musicVolume: number; // 0..1
  qualityLevel: "high" | "medium" | "low" | "off";
  // D-352 (2026-threat-profile-model) — replaces the dead `difficulty` field
  // (no system ever read it, see D-352 for the grep evidence). Identity only
  // in Phase 1; see the ThreatLevelId doc comment above.
  threatLevel: ThreatLevelId;
  /**
   * BL-109 — the persisted text-size multiplier driving `--ui-scale` (DOM) +
   * `getUiScale()` (canvas). A *content* scale composing inside the S113 stage,
   * not a viewport zoom. Default `1` is byte-identical to the pre-control sizes.
   */
  uiScale: number;
}

/** Default audio settings (PT-19). Music defaults quieter than SFX by design. */
export const DEFAULT_SFX_VOLUME = 0.9;
export const DEFAULT_MUSIC_VOLUME = 0.6;

/** S130 (Story Bible §8): a run's ending of record. "purged" = the campaign was
 *  won (ECHELON purged from this node at least once); "resident" = a run fell in
 *  Act II/III (she kept the node). Act-I defeats record nothing — she never
 *  surfaced. Read by the pre-run dossier line. */
export type RunEnding = "purged" | "resident";

export interface StoryProgress {
  /** Distinct endings reached across all runs (deduplicated). The dead
   *  PURGE/MERGE choice fields were dropped in the v5 migration. */
  endingsReached: RunEnding[];
}

export interface MetaState {
  schemaVersion: number;
  unlockedModules: ModuleId[];
  unlockedOperators: OperatorId[];
  achievements: Achievement[];
  highscores: HighscoreEntry[];
  settings: GameSettings;
  storyProgress: StoryProgress;
  tutorialSeen: boolean;
  playerInitials: string;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

const THREAT_LEVEL_ID_SET = new Set<string>(THREAT_LEVEL_IDS);

/** AC-13 (`2026-threat-profile-model`, D-352) — `mergeByTemplate`'s primitive
 *  branch below only checks `typeof`, so any string would pass through as a
 *  `threatLevel` (e.g. a hand-edited or downgraded save). This pins
 *  membership in the five valid IDs. */
function isThreatLevelId(value: unknown): value is ThreatLevelId {
  return typeof value === "string" && THREAT_LEVEL_ID_SET.has(value);
}

/**
 * Deep-merge `value` over a `template`, structurally clamped to the template:
 * - object templates recurse key-by-key (extra keys in `value` are dropped);
 * - array templates keep `value` iff it is an array, else the template default;
 * - primitive templates keep `value` iff `typeof` matches, else the default.
 * `mark()` is called whenever a default has to stand in for a missing or
 * wrong-typed field, so callers can tell a real recovery from a clean blob.
 */
function mergeByTemplate<T>(template: T, value: unknown, mark: () => void): T {
  if (Array.isArray(template)) {
    if (Array.isArray(value)) return value as unknown as T;
    mark();
    return template;
  }
  if (isPlainObject(template)) {
    if (!isPlainObject(value)) {
      mark();
      return template;
    }
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(template)) {
      out[key] = mergeByTemplate(
        (template as Record<string, unknown>)[key],
        value[key],
        mark,
      );
    }
    return out as T;
  }
  // Primitive: accept only a same-typed, non-null value.
  if (value !== null && value !== undefined && typeof value === typeof template) {
    return value as T;
  }
  mark();
  return template;
}

/**
 * BL-86 — coerce a structurally-untrusted parsed blob into a complete, safe
 * {@link MetaState} by deep-merging it over {@link getDefaultMetaState}. Any
 * missing or wrong-typed nested field (`settings`, `highscores`, etc.) falls
 * back to its default, so the boot path can always deref `settings.sfxMuted`
 * &c. without a TypeError.
 *
 * Pure (DOM-free) and **idempotent on a complete valid blob** — a real save
 * round-trips unchanged with `recovered === false`, so there is no behaviour
 * change for healthy data. `recovered === true` flags that a default had to
 * stand in (i.e. the save was partial/garbage and was repaired).
 *
 * This does NOT migrate (no `schemaVersion` change) — it only normalizes a
 * blob already known to be the current schema version.
 *
 * AC-13 (`2026-threat-profile-model`, D-352): also clamps `settings.threatLevel`
 * to one of the five valid {@link ThreatLevelId}s, since `mergeByTemplate`'s
 * generic `typeof` check would let any string through. `MetaStateManager`'s
 * two load paths (current-version and post-migration) both call this
 * function, so one clamp here covers both.
 */
export function normalizeMetaState(parsed: unknown): {
  state: MetaState;
  recovered: boolean;
} {
  let recovered = false;
  const state = mergeByTemplate(getDefaultMetaState(), parsed, () => {
    recovered = true;
  });
  if (!isThreatLevelId(state.settings.threatLevel)) {
    state.settings.threatLevel = DEFAULT_THREAT_LEVEL;
    recovered = true;
  }
  return { state, recovered };
}

/** Default MetaState for a brand new player. */
export function getDefaultMetaState(): MetaState {
  return {
    // Keep in lockstep with MetaStateManager.CURRENT_SCHEMA_VERSION
    // (D-352 / 2026-threat-profile-model bumped it 6 → 7: `difficulty` →
    // `threatLevel`; 2026-threat-gameplay-contract bumped it 7 → 8:
    // `HighscoreEntry.threatLevel`, the score's population key).
    schemaVersion: 8,
    unlockedModules: [
      "overclocked_boot",
      "pattern_recognition",
      "quick_learner",
    ],
    unlockedOperators: ["sentinel-7"],
    achievements: [
      { id: "first_contact", unlocked: false, progress: 0, target: 1 },
      { id: "clean_sweep", unlocked: false, progress: 0, target: 1 },
      { id: "near_death", unlocked: false, progress: 0, target: 1 },
      { id: "card_collector", unlocked: false, progress: 0, target: 35 },
      { id: "veteran", unlocked: false, progress: 0, target: 7 },
      { id: "combo_master", unlocked: false, progress: 0, target: 1 },
      { id: "skill_seeker", unlocked: false, progress: 0, target: 12 },
      { id: "perfect_defense", unlocked: false, progress: 0, target: 1 },
      { id: "hardcore", unlocked: false, progress: 0, target: 1 },
    ],
    highscores: [],
    settings: {
      language: "en",
      sfxMuted: false,
      musicMuted: false,
      sfxVolume: DEFAULT_SFX_VOLUME,
      musicVolume: DEFAULT_MUSIC_VOLUME,
      qualityLevel: "high",
      threatLevel: DEFAULT_THREAT_LEVEL,
      // S146/BL-142 — the app ships at the L presentation (the S/M steps were
      // retired); new saves default straight to the Large scale.
      uiScale: L_UI_SCALE,
    },
    storyProgress: {
      endingsReached: [],
    },
    tutorialSeen: false,
    playerInitials: DEFAULT_INITIALS,
  };
}
